import React, { Component } from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import DataBase from "../../dataBase/liquidGuides";
import { stableEventKey } from "../../Common/GuideDiff";
import PlaceEditModal from "./PlaceEditModal";
import EventEdit from "./EventEdit";
import GuideDiscussionModal from "./GuideDiscussionModal";
import "./Modal.css";

// solid() is a babel macro — it needs a string literal at build time, so it
// can't be called with a dynamic value like a group's own `icon` field
// (that broke the build: "Only string literals are supported when
// referencing icons"). Resolve each one here, with literals, once; index
// into this plain object at render time instead.
const ICONS = {
  comments: solid("comments"),
  "location-dot": solid("location-dot"),
  "calendar-days": solid("calendar-days"),
};

// Jump-list behind both the "you have comments" banner and the persistent
// "Comments" Guide-menu entry — groups every comment in the guide by what
// it's attached to, resolves each target back to the actual Place/Event's
// display name from the currently-loaded guide, and lets you click straight
// to it. A "guide" group (trip-level discussion, if it has any posts yet)
// sorts first, distinct from place/event rows.
class CommentsListModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      groups: [],
      openPlace: null,
      openEvent: null,
      openGuideDiscussion: false,
    };
  }

  componentDidMount() {
    DataBase.getAllGuideComments(this.props.guide_info_id, this.getAllGuideCommentsCallback);
  }

  getAllGuideCommentsCallback = (res) => {
    if (res.pass !== true) {
      this.setState({ loading: false });
      return;
    }
    // Self-heal comment_count — we already paid for the full read this
    // callback is handling, so compare it against the real total and fix
    // the denormalized counter if a comment created before that counting
    // logic existed (or removed by some other path) left it stale. See
    // DataBase.setCommentCount's own comment for why this is safe.
    const trueCount = res.comments.length;
    if (trueCount !== (this.props.comment_count || 0)) {
      DataBase.setCommentCount(this.props.guide_info_id, trueCount, () => {});
    }

    const byKey = new Map();
    res.comments.forEach((c) => {
      const key = `${c.data.target_type}||${c.data.target_id}`;
      if (!byKey.has(key)) {
        byKey.set(key, { target_type: c.data.target_type, target_id: c.data.target_id, count: 0, ids: [] });
      }
      const g = byKey.get(key);
      g.count += 1;
      g.ids.push(c.id);
    });

    const places = window.guide_obj?.places?.placesList || [];
    const events = window.guide_obj?.schedule?.events || [];
    const groups = [...byKey.values()].map((g) => {
      if (g.target_type === "guide") return { ...g, label: "Trip Discussion", icon: "comments" };
      if (g.target_type === "place") {
        const place = places.find((p) => p.ID === g.target_id);
        return { ...g, label: place ? place.Name : "(deleted place)", icon: "location-dot", place };
      }
      const event = events.find((e) => (e.CommentKey || stableEventKey(e)) === g.target_id);
      return { ...g, label: event ? event.Subject : "(deleted event)", icon: "calendar-days", event };
    });
    groups.sort((a, b) => (a.target_type === "guide" ? -1 : b.target_type === "guide" ? 1 : 0));
    this.setState({ loading: false, groups });
  };

  handleClick = (group) => {
    if (group.target_type === "guide") this.setState({ openGuideDiscussion: true });
    else if (group.target_type === "place" && group.place) this.setState({ openPlace: group.place });
    else if (group.target_type === "event" && group.event) this.setState({ openEvent: group.event });
    else {
      // Can't resolve to a live place/event — either it was genuinely
      // deleted, or (for events specifically) these are leftover comments
      // from before Event.js's CommentKey existed, keyed under the old
      // content-based stableEventKey, which no longer matches anything once
      // every event got a real CommentKey. Either way there's nothing to
      // navigate to, so offer the one thing that IS useful: clearing these
      // orphaned comments out (and their share of comment_count).
      const noun = group.target_type === "place" ? "place" : "event";
      if (window.confirm(`This ${noun} no longer exists. Delete its ${group.count} orphaned comment${group.count === 1 ? "" : "s"}?`)) {
        this.deleteOrphanedGroup(group);
      }
    }
  };

  deleteOrphanedGroup = (group) => {
    let pending = group.ids.length;
    group.ids.forEach((id) => {
      DataBase.deleteComment(this.props.guide_info_id, id, () => {
        pending -= 1;
        if (pending === 0) {
          this.setState({ loading: true });
          DataBase.getAllGuideComments(this.props.guide_info_id, this.getAllGuideCommentsCallback);
        }
      });
    });
  };

  render() {
    const { loading, groups, openPlace, openEvent, openGuideDiscussion } = this.state;
    return (
      <div className="modal">
        <Modal {...this.props} size="md" aria-labelledby="contained-modal-title-vcenter" centered scrollable={true}>
          <Modal.Header>
            <div style={{ width: "100%" }}>
              <div className="dialog-heading">
                <div>
                  <h4 style={{ color: "#3598DB" }}>Comments</h4>
                </div>
                <div>
                  <FontAwesomeIcon
                    icon={solid("times")}
                    className="pointer color-blue"
                    onClick={() => this.props.onHide()}
                  ></FontAwesomeIcon>
                </div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            {loading ? (
              <div>Loading…</div>
            ) : groups.length === 0 ? (
              <div>No comments yet.</div>
            ) : (
              groups.map((g) => (
                <div
                  className="list-item-card"
                  key={`${g.target_type}||${g.target_id}`}
                  onClick={() => this.handleClick(g)}
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon icon={ICONS[g.icon]} style={{ marginRight: 10 }} />
                  {g.label}
                  <span style={{ float: "right", color: "#71717A" }}>{g.count}</span>
                </div>
              ))
            )}
          </Modal.Body>
        </Modal>

        {openPlace ? (
          <PlaceEditModal
            show={true}
            onHide={() => this.setState({ openPlace: null })}
            place={openPlace}
            user_info={this.props.user_info}
            mode={"Edit"}
            update_display={() => {}}
          />
        ) : null}
        {openEvent ? (
          <EventEdit
            show={true}
            onHide={() => this.setState({ openEvent: null })}
            event={openEvent}
            mode={"MODIFY"}
            callback={() => {}}
          />
        ) : null}
        {openGuideDiscussion ? (
          <GuideDiscussionModal show={true} onHide={() => this.setState({ openGuideDiscussion: false })} />
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  guide_info_id: state.guide_meta.guide_info_id,
  comment_count: state.guide_meta.guide_info?.comment_count,
  user_info: state.user_info,
});

export default connect(mapStateToProps)(CommentsListModal);
